import React, {VoidFunctionComponent} from 'react';
import {useHistory, useParams, Redirect} from 'react-router-dom';
import {useIntl} from 'react-intl';
import useSWR from 'swr';

import PhotoGallery from '../photo-gallery/photo-gallery';
import LoadingBar from '../loading-bar/loading-bar';
import IconButton from '../icon-button/icon-button';
import CloseIcon from '../icons/close/close';
import Tags from '../tags/tags';

import getLocalizedText from '../../libs/get-localized-text';
import convertTagsStringToArray from '../../libs/convert-tags-string-to-array';

import {Work, WorkId} from '../../../../../shared/types/work';

import styles from './work-of-art-details.module.css';

const WorkOfArtDetails: VoidFunctionComponent<Record<string, unknown>> = () => {
  const {formatMessage, locale} = useIntl();
  const history = useHistory();
  const {workId} = useParams<{workId: WorkId}>();

  // Fetches the work data for the passed id
  const {data: work, error: workError} = useSWR<Work>(['works', workId]);

  const returnToHomeScreen = () => {
    history.push('/');
  };

  if (workError) {
    return <Redirect to="/" />;
  }

  if (!work) {
    return <LoadingBar />;
  }

  const localeSponsor = getLocalizedText(work.sponsor, locale);
  const localePhotoCredits = getLocalizedText(work.photoCredits, locale);

  // Convert work's tags data
  const localeTags = getLocalizedText(work.tags, locale);
  const workOfArtTags = convertTagsStringToArray(localeTags);

  return (
    <div className={styles.container}>
      <IconButton
        className={styles.closeButton}
        variant="secondary"
        onClick={returnToHomeScreen}>
        <CloseIcon />
      </IconButton>

      <div className={styles.photos}>
        <PhotoGallery
          photos={work.photos}
          photoCredits={getLocalizedText(work.photoCredits, locale)}
        />
      </div>

      <div className={styles.info}>
        <Tags tags={workOfArtTags} />

        <div className={styles.metadata}>
          <h3>
            {work.artist}
            <br />
            {work.title}
          </h3>
          <p>{work.year}</p>
          <p>
            {work.street}
            {work.street && (work.postalCode || work.city) ? ', ' : ''}
            {work.postalCode}
            {work.postalCode && work.city ? <>&nbsp;</> : ''}
            {work.city}
          </p>
          <p>{formatMessage({id: `work.category.${work.category}`})}</p>
          <p className={styles.description}>
            {getLocalizedText(work.description, locale)}
          </p>

          {localeSponsor && (
            <p className={styles.sponsor}>
              {formatMessage({id: 'work.sponsor'})}
              :&#32;{localeSponsor}
            </p>
          )}
          {localePhotoCredits && (
            <p>
              {formatMessage({id: 'work.photo'})}
              :&#32;{localePhotoCredits}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOfArtDetails;
